import React, { useState } from 'react';

import { Box, Typography } from '@mui/material';

import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { InsuranceLabs } from '../../tasks/types/insuranceLabs';
import styles from '../styles.module.scss';
import { Feedback } from './components/Feedback';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { Remarks } from './components/Remarks';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

export const InsuranceLabsDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  feedbackHandler,
  deleteHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  feedbackHandler: (
    taskId: number,
    sufficent: boolean,
    feedback: string,
  ) => void;
}) => {
  const [needsFeedback, setneedsFeedback] = useState(false);
  const data: InsuranceLabs = task.taskData as InsuranceLabs;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      if (
        (needsFeedback === false && state === TaskState.COMPLETED) ||
        needsFeedback
      ) {
        setneedsFeedback(true);
        return;
      }
      taskStateHandler(task.id, state, reason);
    }
  };

  const onFeedback = async (
    sufficient: boolean,
    text: string,
    noOperation: boolean,
  ) => {
    if (noOperation) {
      setneedsFeedback(false);
      return;
    }
    setneedsFeedback(false);
    feedbackHandler(task.id, sufficient, text);
  };

  return (
    <Box data-label={ElementLocators.LOCATOR_INSURANCE_LABS_SECTION}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        mb={0.75}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={0.5}>
            <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
          </Box>
          <Typography variant="h2">
            {Resource.TASK_TITLE_INSURANCE_LABS}
          </Typography>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
          </Typography>
        </Box>
      </Box>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {needsFeedback ? (
        <Feedback show={needsFeedback} feedbackHandler={onFeedback}></Feedback>
      ) : task.sufficient != null ? (
        <Remarks
          sufficient={task.sufficient}
          feedback={task.feedback}
        ></Remarks>
      ) : (
        <></>
      )}

      <TaskDetailsItem
        leftItem="Approx. lab date"
        rightItem={data?.approxDateOfLabs || Resource.NA}
      />

      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}

      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED && (
          <FileUpload
            leftItem={evidenceExists(evidence) ? '' : Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.INSURANCE_LABS}
            ongoingUpload={task.ongoingUpload?.[DocumentType.INSURANCE_LABS]}
          />
        )}
      {task.state !== TaskState.REQUESTED && (
        <TaskNote taskID={task.id} originalNote={data?.note} />
      )}

      {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {data?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={data.reasonDescription}
            />
          )}
        </>
      )}
    </Box>
  );
};
